/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { Button, Text, theme, FlexContainer } from "@nordcloud/gnui";
import { useApplicationQuery } from "~/generated/graphql";
import { ErrorMessage, LoaderWrap, DataDisplayWrapper } from "~/components";
import { dateFormat } from "~/constants";
import { useUuidValidator } from "~/hooks";
import { useUserflow } from "~/services/userflow";
import { USERFLOW_CONTENT_IDS } from "~/services/userflow/config";
import { isNotEmpty } from "~/tools";
import { RouteParams } from "../types";
import { EnvironmentItem } from "./EnvironmentItem";
import { useEnvironments } from "./hooks";

export function Environments() {
  const { application } = useParams<RouteParams>();
  useUuidValidator(application);

  const { data, error, loading } = useApplicationQuery({
    variables: { id: application, year: dayjs().format(dateFormat.year) },
    fetchPolicy: "network-only",
  });

  const { environments } = useEnvironments(data);

  const hasData = error == null && isNotEmpty(environments);

  return (
    <LoaderWrap inContent loading={loading}>
      <ErrorMessage error={error} />
      <DataDisplayWrapper
        messageComponent={<NoEnvironmentData />}
        hasData={hasData}
      >
        {environments.map((env) => (
          <EnvironmentItem key={env.id} environment={env} appId={application} />
        ))}
      </DataDisplayWrapper>
    </LoaderWrap>
  );
}

function NoEnvironmentData() {
  const flowId = USERFLOW_CONTENT_IDS.NoEnvironment;
  const { start } = useUserflow();

  return (
    <FlexContainer direction="column">
      <Text css={{ fontWeight: theme.fontWeights.bold }} mb="0">
        There are no environments
      </Text>
      <Text>
        For information on how to create one, please refer to the documentation
      </Text>
      <Button color="notification" onClick={() => start(flowId)}>
        Adding an Application Environment
      </Button>
    </FlexContainer>
  );
}
